import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { blogData } from '../data/mockData';

const FeaturedPost = ({ post }) => {
  return (
    <article className="group cursor-pointer">
      <Link to={`/blog/${post.slug}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Image */}
          <div className="aspect-[16/10] overflow-hidden image-hover border border-[#2a2a2a]">
            <img 
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Content */}
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-xs font-medium uppercase tracking-wider text-[#0a0a0a] bg-[#c9a962] px-3 py-1">
                {post.categoryLabel}
              </span>
              <span className="body-small text-[#808080]">{post.date}</span>
            </div>
            
            <h3 className="heading-2 text-white mb-4 group-hover:text-[#c9a962] transition-colors">
              {post.title}
            </h3>
            
            <p className="body-large text-[#b3b3b3] mb-6">
              {post.excerpt}
            </p>
            
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2 body-small text-[#808080]">
                <Clock size={14} />
                {post.readTime}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-[#c9a962] group-hover:gap-3 transition-all">
                Read More
                <ArrowRight size={16} />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
};

const BlogCard = ({ post }) => {
  return (
    <article className="group cursor-pointer">
      <Link to={`/blog/${post.slug}`}>
        <div className="aspect-[16/10] overflow-hidden mb-4 image-hover border border-[#2a2a2a]">
          <img 
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex items-center gap-4 mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-[#c9a962]">
            {post.categoryLabel}
          </span>
          <span className="body-small text-[#808080]">{post.date}</span>
        </div>
        
        <h3 className="heading-3 text-white mb-3 group-hover:text-[#c9a962] transition-colors">
          {post.title}
        </h3>
        
        <p className="body-regular text-[#b3b3b3] mb-4 line-clamp-2">
          {post.excerpt}
        </p>
        
        <span className="flex items-center gap-2 body-small text-[#808080]">
          <Clock size={14} />
          {post.readTime}
        </span>
      </Link>
    </article>
  );
};

const Blog = () => {
  const featuredPost = blogData.find(post => post.featured);
  const regularPosts = blogData.filter(post => !post.featured).slice(0, 3);

  return (
    <section id="blog" className="section-padding bg-[#111111]">
      <div className="container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-sm font-medium text-[#c9a962] tracking-wider uppercase mb-4">
              Insights
            </span>
            
            <h2 className="heading-1 text-white">
              Latest Articles
            </h2>
          </div>
          
          <Button 
            asChild
            variant="outline"
            className="bg-transparent text-[#c9a962] border border-[#c9a962] hover:bg-[#c9a962] hover:text-[#0a0a0a] rounded-none px-6 py-3 h-auto text-sm font-medium transition-all duration-300 w-fit"
          >
            <Link to="/blog" className="inline-flex items-center gap-2">
              View All Articles
              <ArrowRight size={16} />
            </Link>
          </Button>
        </div>
        
        {/* Featured Post */}
        {featuredPost && (
          <div className="mb-16 pb-16 border-b border-[#2a2a2a]">
            <FeaturedPost post={featuredPost} />
          </div>
        )}
        
        {/* Regular Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {regularPosts.map((post) => (
            <BlogCard key={post.id} post={post} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Blog;
